import * as React from 'react';
import styled from 'styled-components';
import { colorsV2 } from 'style/colors-v2';
import { Typography } from 'components/DesignSystemV2/Typography';
import { LazyImage } from 'components/LazyImage';
import { VideoThumbnail } from 'components/VideoThumbnail';
import { toScreen } from 'utils/media-query/responsive.util';
import {
  CrossIcon,
  CheckIcon,
  IconsContentQuote
} from 'components/Icon/SVGIcons';
import { getAssetLinkByHandle } from 'utils/getAssetLink';
import { VideoModal } from 'components/VideoModal';
import { useModal } from 'hooks/useModal';
import { useState } from 'react';
import Arrow from './icons/Arrow';
import { desktopFirstV3 } from 'style/breakpointsv3';

const METHODS = {
  tranditional: 'tranditional',
  linear: 'linearthinking'
};

const Main = styled.div``;
const Left = styled.div`
  max-width: 500px;
  margin-bottom: 48px;
`;

const SubHeadline = styled(Typography)`
  margin-bottom: 16px;
`;

const Right = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 48px;

  ${toScreen(desktopFirstV3.md)} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ColumnTitle = styled(Typography)`
  &.${METHODS.tranditional} {
    u {
      color: ${colorsV2.yellow120};
    }
  }

  &.${METHODS.linear} {
    u {
      color: ${colorsV2.green120};
    }
  }
`;

const ContentList = styled.ul`
  display: grid;
  gap: 24px;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContentItem = styled.li`
  display: flex;
  align-items: center;
  padding: 20px;
  gap: 16px;
  border-radius: 20px;
  box-shadow: 0px 12px 24px 0px #30427814, 0px 3px 6px 0px #3042780f;
  border: 2px solid ${colorsV2.white100};

  &.${METHODS.tranditional} {
    background-color: ${colorsV2.yellow120};

    .emoji {
      transform: translateY(-10px);
    }

    &:hover {
      .emoji {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
  &.${METHODS.linear} {
    background-color: ${colorsV2.green120};

    .emoji {
      transform: scale(0.8);
    }

    &:hover {
      .emoji {
        opacity: 1;
        transform: scale(1);
      }
    }
  }

  &:hover {
    svg {
      transform: scale(0);
      opacity: 0;
    }
  }
`;

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ArrowWrapper = styled.div`
  flex-shrink: 0;

  ${toScreen(desktopFirstV3.md)} {
    display: none;
  }
`;

const EmojiWrapper = styled(IconWrapper)`
  position: relative;

  .emoji {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    transition: 0.3s ease-out;
    box-shadow: 0px 24px 32px 0px #3042781a, 0px 16px 24px 0px #30427829,
      0px 4px 8px 0px #30427829;
    border-radius: 100px;
  }

  svg {
    transition: 0.3s ease-out;
  }
`;

const VideoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 480px;
`;

const ColumnContent = ({
  method,
  title,
  contents,
  footer,
  hasArrow = false
}) => {
  const iconProps = { withBackground: false };
  return (
    <ColumnWrapper>
      <ColumnHeader>
        <ColumnTitle
          variant="semi-bold/24-32"
          color={colorsV2.neutral180}
          className={method}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {hasArrow && (
          <ArrowWrapper>
            <Arrow />
          </ArrowWrapper>
        )}
      </ColumnHeader>
      <ContentList>
        {contents.map(({ content, emoji }, index) => (
          <ContentItem key={index} className={method}>
            <EmojiWrapper>
              {method === METHODS.tranditional ? (
                <CrossIcon {...iconProps} />
              ) : (
                <CheckIcon {...iconProps} />
              )}
              <LazyImage className="emoji" handle={emoji} />
            </EmojiWrapper>
            <Typography variant="semi-bold/16-24" color={colorsV2.white100}>
              {content}
            </Typography>
          </ContentItem>
        ))}
      </ContentList>
      {footer}
    </ColumnWrapper>
  );
};

const Quote = styled.blockquote`
  display: flex;
  gap: 16px;
  max-width: 372px;
  padding: 0;
  margin: 0;
`;

const LinearthinkingDifferences = ({ data }) => {
  const { isOpened, openModal, closeModal } = useModal();
  const [videoUrl, setVideoUrl] = useState('');

  const { title, description, traditional, linear } = data;
  return (
    <Main>
      <Left>
        <SubHeadline variant="medium/16-24" color={colorsV2.gray120}>
          {description}
        </SubHeadline>
        <Typography
          variant="bold/24-32"
          tabletVariant="bold/32-40"
          desktopVariant="bold/40-48"
          color={colorsV2.black100}
          v3
        >
          {title}
        </Typography>
      </Left>
      <Right>
        <ColumnContent
          hasArrow
          method={METHODS.tranditional}
          title={traditional.title}
          contents={traditional.contents}
          footer={
            <Quote>
              <IconWrapper>
                <IconsContentQuote size={36} />
              </IconWrapper>
              <Typography variant="regular/16-24" color={colorsV2.neutral140}>
                {traditional.quote}
              </Typography>
            </Quote>
          }
        />
        <ColumnContent
          method={METHODS.linear}
          title={linear.title}
          contents={linear.contents}
          footer={
            <VideoWrapper>
              <VideoThumbnail
                width={144}
                poster={getAssetLinkByHandle({
                  handle: linear.video.thumbnailHandle
                })}
                onClick={() => {
                  setVideoUrl(linear.video.url);
                  openModal();
                }}
                iconSize={40}
              />
              <Typography variant="regular/16-24" color={colorsV2.neutral140}>
                {linear.video.caption}
              </Typography>
            </VideoWrapper>
          }
        />
      </Right>
      <VideoModal
        isOpened={isOpened}
        onClose={() => {
          closeModal();
          setVideoUrl('');
        }}
        url={videoUrl}
      />
    </Main>
  );
};

export default LinearthinkingDifferences;
